/**
 * 🔌 ADMIN API SERVICE - Integration with new backend APIs
 * Handles admin endpoints for users hub and driver approvals
 */

// API Base URL
import { getApiBaseUrl } from '@/config/environment';


// Types for Users Hub API
export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role?: 'user' | 'driver' | 'customer' | 'admin'; // Primary role from database
  userType: 'renter' | 'host' | 'driver';
  isVerified: boolean;
  driverVerification?: {
    id: string;
    status: 'draft' | 'pending_review' | 'approved' | 'rejected';
    completionPercentage: number;
  };
  createdAt?: string;
  phone?: string;
  location?: string;
  roles?: string[]; // Additional roles array
}

export interface UsersHubStats {
  total: number;
  registeredToday: number;
  verified: number;
  pendingVerification?: number;
  multiRoleUsers?: number;
}

export interface UsersHubResponse {
  success: boolean;
  data: {
    users: AdminUser[];
    statistics: UsersHubStats;
  };
}

// Types for Driver Approvals API
export interface AdminDriver {
  id: string; // verification_id
  userId: string;
  userName: string;
  userEmail?: string;
  userPhone?: string;
  status: 'draft' | 'pending_review' | 'approved' | 'rejected';
  completionPercentage: number;
  canApprove: boolean;
  driverType?: 'independent' | 'host_driver'; // Type of driver
  employerId?: string; // ID of fleet owner/host if host driver
  employerName?: string; // Name of fleet owner/host
  identity: {
    nin: string;
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    identityPhotoUrl?: string;
    selfiePhotoUrl?: string;
  };
  license: {
    number: string;
    expiryDate: string;
    state?: string;
    frontPhotoUrl?: string;
    backPhotoUrl?: string;
  };
  documents?: {
    type: string;
    status: 'pending' | 'approved' | 'rejected';
    url?: string;
  }[];
  submittedAt?: string;
  reviewedAt?: string;
}

export interface DriverApprovalsStats {
  pendingReview: number;
  approved: number;
  rejected: number;
  total?: number;
}

export interface DriverApprovalsResponse {
  success: boolean;
  data: {
    drivers: AdminDriver[];
    statistics: DriverApprovalsStats;
  };
}

export const adminApiService = {
  
  /**
   * 👥 Get all users for Users Hub
   */
  async getUsers(filters?: {
    userType?: 'renter' | 'host' | 'driver';
    isVerified?: boolean;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<UsersHubResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.userType) queryParams.append('userType', filters.userType);
      if (filters?.isVerified !== undefined) queryParams.append('isVerified', filters.isVerified.toString());
      if (filters?.page) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.search) queryParams.append('search', filters.search);

      const url = `${getApiBaseUrl()}/api/v1/admin/users${queryParams.toString() ? `?${queryParams}` : ''}`;


      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });


      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ HTTP ${response.status}:`, errorText);

        // Return success: true with empty data instead of throwing
        // This prevents the error message from showing
        return {
          success: true,
          data: {
            users: [],
            statistics: {
              total: 0,
              registeredToday: 0,
              verified: 0,
              pendingVerification: 0,
              multiRoleUsers: 0
            }
          }
        };
      }

      const result = await response.json();

      // If backend returns success field, use it; otherwise assume success
      if (result.success !== undefined) {
        return result;
      }

      // Transform backend response to expected format if needed
      return {
        success: true,
        data: result.data || result
      };
    } catch (error) {
      console.error('❌ Network error fetching users:', error);

      // Return success: true with empty data to prevent error message
      return {
        success: true,
        data: {
          users: [],
          statistics: {
            total: 0,
            registeredToday: 0,
            verified: 0,
            pendingVerification: 0,
            multiRoleUsers: 0
          }
        }
      };
    }
  },

  /**
   * 🚗 Get host drivers for approval dashboard
   */
  async getHostDrivers(filters?: {
    status?: 'pending' | 'approved' | 'rejected';
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<DriverApprovalsResponse> {
    try {

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.page) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());

      const queryString = queryParams.toString();
      const url = `${getApiBaseUrl()}/api/admin/host-drivers${queryString ? `?${queryString}` : ''}`;


      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Host Drivers API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API returned success: false');
      }

      // Transform host drivers data to AdminDriver format
      const drivers = (result.data?.drivers || result.drivers || []).map((driver: any) => ({
        id: driver.driverId?.toString() || driver.id,
        userId: driver.hostId || driver.userId,
        userName: `${driver.firstName || ''} ${driver.lastName || ''}`.trim(),
        userEmail: driver.email,
        userPhone: driver.phone,
        // Map backend status to frontend status
        status: driver.status === 'active' ? 'approved' :
                driver.status === 'pending' ? 'pending_review' :
                driver.status === 'rejected' ? 'rejected' :
                !driver.isVerified ? 'pending_review' : 'approved',
        completionPercentage: driver.isVerified ? 100 :
                              (driver.licenseNumber && driver.idCardNumber) ? 80 :
                              driver.licenseNumber ? 60 : 40,
        canApprove: !driver.isVerified && driver.licenseNumber,
        driverType: 'host_driver',
        employerId: driver.hostId,
        employerName: driver.host ? `${driver.host.firstName || ''} ${driver.host.lastName || ''}`.trim() : '',
        identity: {
          nin: driver.idCardNumber || '',
          firstName: driver.firstName || '',
          lastName: driver.lastName || '',
          dateOfBirth: driver.dateOfBirth || '',
          identityPhotoUrl: driver.idCardPhotoUrl || '',
          selfiePhotoUrl: driver.profilePhotoUrl || ''
        },
        license: {
          number: driver.licenseNumber || '',
          expiryDate: driver.licenseExpiryDate || '',
          state: driver.licenseClass || '',
          frontPhotoUrl: driver.licensePhotoUrl || '',
          backPhotoUrl: ''
        },
        documents: [],
        submittedAt: driver.createdAt,
        reviewedAt: driver.reviewedAt
      }));

      const statistics: DriverApprovalsStats = {
        pendingReview: result.data?.statistics?.pending || drivers.filter((d: any) => d.status === 'pending_review').length,
        approved: result.data?.statistics?.approved || drivers.filter((d: any) => d.status === 'approved').length,
        rejected: result.data?.statistics?.rejected || drivers.filter((d: any) => d.status === 'rejected').length,
        total: drivers.length
      };

      return {
        success: true,
        data: {
          drivers,
          statistics
        }
      };
    } catch (error) {
      console.error('❌ Error fetching host drivers:', error);
      return {
        success: false,
        data: {
          drivers: [],
          statistics: {
            pendingReview: 0,
            approved: 0,
            rejected: 0,
            total: 0
          }
        }
      };
    }
  },

  /**
   * 🚗 Get drivers for approval dashboard
   */
  async getDrivers(filters?: {
    status?: 'draft' | 'pending_review' | 'approved' | 'rejected';
    page?: number;
    limit?: number;
    search?: string;
    type?: string; // Filter by approval type
  }): Promise<DriverApprovalsResponse> {
    try {

      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.type) queryParams.append('type', filters.type);
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.page) queryParams.append('page', filters.page.toString());
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());

      const queryString = queryParams.toString();
      const url = `${getApiBaseUrl()}/api/v1/admin/approvals${queryString ? `?${queryString}` : ''}`;


      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Approvals API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'API returned success: false');
      }

      // Transform approvals data to drivers format
      const approvals = result.data?.approvals || [];

      // ✅ CRITICAL: Filter for driver verifications only (not vehicles or business approvals)
      const driverApprovals = approvals.filter((approval: any) => approval.type === 'driver_verification');


      const drivers = driverApprovals.map((approval: any) => {
        // Check various possible field names for employer info
        const employerId = approval.employerId || approval.employer_id || approval.hostId || approval.host_id || approval.fleetOwnerId;
        const employerName = approval.employerName || approval.employer_name || approval.hostName || approval.host_name || approval.fleetOwnerName;


        return {
          id: approval.id,
          userId: approval.applicant.id,
          userName: approval.applicant.name,
          userEmail: approval.applicant.email,
          userPhone: approval.applicant.phone,
          // Map drivers table status to frontend status
          status: approval.status === 'pending_verification' ? 'pending_review' : approval.status,
          completionPercentage: approval.completionPercentage || 0,
          canApprove: approval.actions.canApprove,
          driverType: employerId ? 'host_driver' : 'independent',
          employerId: employerId,
          employerName: employerName,
          identity: {
            nin: approval.documents?.identity?.nin || '',
            firstName: approval.applicant.name.split(' ')[0] || '',
            lastName: approval.applicant.name.split(' ').slice(1).join(' ') || '',
            dateOfBirth: '',
            ninPhotoUrl: approval.documents?.identity?.ninPhoto || '',
            selfiePhotoUrl: approval.applicant.profilePictureUrl || approval.documents?.identity?.selfiePhoto || ''
          },
          license: {
            number: approval.documents?.license?.number || '',
            expiryDate: approval.documents?.license?.expiryDate || '',
            state: '',
            frontPhotoUrl: approval.documents?.license?.frontPhoto || '',
            backPhotoUrl: approval.documents?.license?.backPhoto || ''
          },
          documents: [
            { type: 'identity', status: 'pending', url: approval.documents?.identity?.ninPhoto },
            { type: 'selfie', status: 'pending', url: approval.documents?.identity?.selfiePhoto },
            { type: 'license_front', status: 'pending', url: approval.documents?.license?.frontPhoto },
            { type: 'license_back', status: 'pending', url: approval.documents?.license?.backPhoto }
          ].filter(doc => doc.url),
          submittedAt: approval.submittedAt,
          reviewedAt: approval.reviewed_at
        };
      });

      // Apply frontend filters
      const filteredDrivers = drivers.filter((driver: any) => {
        if (filters?.status && filters.status !== 'all' && driver.status !== filters.status) {
          return false;
        }
        return true;
      });

      const statistics: DriverApprovalsStats = {
        pendingReview: result.data?.statistics?.pendingReview || 0,
        approved: result.data?.statistics?.approved || 0,
        rejected: result.data?.statistics?.rejected || 0,
        total: result.data?.statistics?.totalPending || 0
      };

      return {
        success: true,
        data: {
          drivers: filteredDrivers,
          statistics
        }
      };
    } catch (error) {
      console.error('❌ Error fetching drivers:', error);

      return {
        success: false,
        data: {
          drivers: [],
          statistics: {
            pendingReview: 0,
            approved: 0,
            rejected: 0,
            total: 0
          }
        }
      };
    }
  },

  /**
   * ✅ Approve a host driver application
   */
  async approveHostDriver(id: string, adminNote?: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/host-drivers/${id}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminNote
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: result.message || 'Host driver approved successfully'
      };
    } catch (error) {
      console.error('❌ Error approving host driver:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to approve host driver'
      };
    }
  },

  /**
   * ❌ Reject a host driver application
   */
  async rejectHostDriver(id: string, rejectionReason: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/host-drivers/${id}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejectionReason
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: result.message || 'Host driver rejected successfully'
      };
    } catch (error) {
      console.error('❌ Error rejecting host driver:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reject host driver'
      };
    }
  },

  /**
   * ✅ Approve driver verification
   */
  async approveDriver(verificationId: string, notes?: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/approvals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'approve',
          verificationId,
          notes
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Approve Driver API Error ${response.status}:`, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error approving driver:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to approve driver'
      };
    }
  },

  /**
   * ❌ Reject driver verification
   */
  async rejectDriver(verificationId: string, reason: string, notes?: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/approvals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'reject',
          verificationId,
          notes: `${reason}${notes ? `\n\n${notes}` : ''}`
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Reject Driver API Error ${response.status}:`, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error rejecting driver:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reject driver'
      };
    }
  },

  /**
   * 👤 Update user information
   */
  async updateUser(userId: string, data: Partial<AdminUser>): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: 'User updated successfully'
      };
    } catch (error) {
      console.error('❌ Error updating user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update user'
      };
    }
  },

  /**
   * 💬 Send message to user
   */
  async sendMessageToUser(userId: string, message: string, subject?: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users/${userId}/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: subject || 'Admin Message',  // Backend expects "title", not "subject"
          message,
          type: 'admin',
          priority: 'normal'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
          console.error('❌ Backend error response:', {
            status: response.status,
            error: errorData.error,
            message: errorData.message,
            fullResponse: errorData
          });
        } catch {
          console.error('❌ Backend error response (raw):', errorText);
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      return {
        success: true,
        message: result.message || 'Message sent successfully'
      };
    } catch (error) {
      console.error('❌ Error sending message:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send message'
      };
    }
  },

  /**
   * 🚫 Suspend user
   */
  async suspendUser(userId: string, reason: string, duration?: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'suspend', reason, duration })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: 'User suspended successfully'
      };
    } catch (error) {
      console.error('❌ Error suspending user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to suspend user'
      };
    }
  },

  /**
   * ✅ Unsuspend user
   */
  async unsuspendUser(userId: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'unsuspend' })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: 'User unsuspended successfully'
      };
    } catch (error) {
      console.error('❌ Error unsuspending user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to unsuspend user'
      };
    }
  },

  /**
   * ⚠️ Delete user (TESTING ONLY - PERMANENT)
   */
  async deleteUser(userId: string, reason: string, adminId: string, confirmed: boolean, forceDelete: boolean = false): Promise<{
    success: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> {
    try {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');

      // Debug auth state
      console.group('🔐 Delete User Request - Auth Debug');
      console.log('Token present:', !!token);
      console.log('Token preview:', token ? `${token.substring(0, 20)}...` : 'null');
      if (user) {
        try {
          const parsedUser = JSON.parse(user);
          console.log('Current user:', parsedUser);
          console.log('User role:', parsedUser.role);
        } catch (e) {
          console.warn('Could not parse user from localStorage');
        }
      }
      console.log('Request URL:', `${getApiBaseUrl()}/api/v1/admin/users/${userId}/delete`);
      console.log('Request payload:', { reason, adminId, confirmed, forceDelete });
      console.groupEnd();

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users/${userId}/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason, adminId, confirmed, forceDelete })
      });

      // Log response details
      console.log('Delete user response status:', response.status);
      console.log('Delete user response headers:', Object.fromEntries(response.headers.entries()));

      const result = await response.json();
      console.log('Delete user response body:', result);

      if (!response.ok) {
        // Specific handling for 401 Unauthorized
        if (response.status === 401) {
          console.error('🚫 401 Unauthorized - Token may be invalid, expired, or user lacks admin permissions');
          console.error('Response:', result);

          // Check if we're in dev mode
          const isDev = getApiBaseUrl().includes('localhost');
          const errorMessage = isDev
            ? 'Dev Mode: Token invalid for local backend. Run clearDevAuth() in console and login again, or switch to production environment.'
            : result.error || 'Unauthorized: Please check your admin permissions and try logging in again';

          if (isDev) {
            console.log('%c💡 SOLUTION:', 'color: #00ff00; font-weight: bold; font-size: 14px');
            console.log('1. Open browser console');
            console.log('2. Run: clearDevAuth()');
            console.log('3. Login again with dev credentials');
            console.log('OR switch to production environment where your token works');
          }

          return {
            success: false,
            error: errorMessage,
            data: result.details
          };
        }

        // Parse the error response from API
        return {
          success: false,
          error: result.error || `HTTP error! status: ${response.status}`,
          data: result.details
        };
      }

      return {
        success: true,
        message: 'User permanently deleted',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error deleting user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete user'
      };
    }
  },

  /**
   * ✅ Bulk approve users
   */
  async bulkApproveUsers(userIds: string[], notes?: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users/bulk-approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds, notes })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: `${userIds.length} users approved successfully`
      };
    } catch (error) {
      console.error('❌ Error bulk approving users:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to approve users'
      };
    }
  },

  /**
   * ❌ Bulk reject users
   */
  async bulkRejectUsers(userIds: string[], reason: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users/bulk-reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds, reason })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: `${userIds.length} users rejected successfully`
      };
    } catch (error) {
      console.error('❌ Error bulk rejecting users:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reject users'
      };
    }
  },

  /**
   * 🚫 Bulk suspend users
   */
  async bulkSuspendUsers(userIds: string[], reason: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users/bulk-suspend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds, reason })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: `${userIds.length} users suspended successfully`
      };
    } catch (error) {
      console.error('❌ Error bulk suspending users:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to suspend users'
      };
    }
  },

  /**
   * ⚠️ Bulk delete users (TESTING ONLY - PERMANENT)
   */
  async bulkDeleteUsers(
    userIds: string[],
    reason: string,
    adminId: string,
    confirmed: boolean,
    forceDelete: boolean = false
  ): Promise<{
    success: boolean;
    message?: string;
    data?: any;
    error?: string;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds, reason, adminId, confirmed, forceDelete })
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || `HTTP error! status: ${response.status}`,
          data: result.details
        };
      }

      return {
        success: true,
        message: `${userIds.length} users permanently deleted`,
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error bulk deleting users:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to bulk delete users'
      };
    }
  },

  /**
   * ➕ Create new user
   */
  async createUser(userData: {
    name: string;
    email: string;
    phone: string;
    userType: 'renter' | 'host' | 'driver';
    location?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    data?: AdminUser;
  }> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Create User API Error ${response.status}:`, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        message: 'User created successfully',
        data: result.data
      };
    } catch (error) {
      console.error('❌ Error creating user:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create user'
      };
    }
  }
};